/** Promise-based confirm and prompt that actually work in the native shells.
 *
 *  `window.confirm()` and `window.prompt()` silently return undefined inside the
 *  macOS and iOS WKWebView shells — the coordinators implement none of the
 *  WKUIDelegate JS panel methods — so `if (!confirm(...)) return;` takes the
 *  cancel branch every time and the action never runs. No dialog, no error,
 *  nothing. The same is true in installed PWAs on some platforms.
 *
 *  This renders an in-DOM AlertDialog and resolves a promise, so a call site
 *  migrates with a one-word diff:
 *
 *      if (!confirm('Delete this?')) return;              // broken in the shell
 *      if (!await confirmDialog({ body: 'Delete this?' })) return;   // works
 *
 *  Mounted once in AppProviders; `useConfirm()` reaches it from anywhere. */
import {
  createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode,
} from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './alert-dialog';
import { Input } from './input';

export interface ConfirmOptions {
  /** Defaults to "Are you sure?" — prefer a specific question. */
  title?: string;
  body?: ReactNode;
  /** Defaults to "Confirm". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button in the danger colour. Use for anything that
   *  deletes or is otherwise not undoable. */
  destructive?: boolean;
  /** Turns this into a prompt: a single text field whose value resolves instead
   *  of `true`. Cancel resolves null. */
  prompt?: { label?: string; placeholder?: string; initialValue?: string };
}

/** How a question was answered: its confirm button, its cancel button, or
 *  closed some other way (Escape, a tap outside). */
export type ConfirmChoice = 'confirm' | 'cancel' | 'dismiss';

// The cancel button's own answer, so that a question whose cancel button is an
// action in its own right can tell it from the dialog simply being closed.
// Every other caller still reads it as not confirmed.
const CANCELLED = Symbol('cancelled');

type ConfirmResult = boolean | string | null | typeof CANCELLED;
type Resolver = (value: ConfirmResult) => void;

interface ConfirmContextValue {
  open: (options: ConfirmOptions) => Promise<ConfirmResult>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [value, setValue] = useState('');
  const resolverRef = useRef<Resolver | null>(null);

  const open = useCallback((next: ConfirmOptions) => {
    setValue(next.prompt?.initialValue ?? '');
    setOptions(next);
    return new Promise<ConfirmResult>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  // Every close path must settle the promise, or an awaiting call site hangs
  // forever holding whatever state it set before asking.
  const settle = useCallback((result: ConfirmResult) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOptions(null);
    resolve?.(result);
  }, []);

  const ctx = useMemo(() => ({ open }), [open]);
  const isPrompt = !!options?.prompt;

  return (
    <ConfirmContext.Provider value={ctx}>
      {children}
      <AlertDialog
        open={!!options}
        onOpenChange={(next) => { if (!next) settle(isPrompt ? null : false); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{options?.title ?? 'Are you sure?'}</AlertDialogTitle>
            {options?.body && (
              <AlertDialogDescription>{options.body}</AlertDialogDescription>
            )}
          </AlertDialogHeader>

          {isPrompt && (
            <Input
              autoFocus
              value={value}
              placeholder={options?.prompt?.placeholder}
              aria-label={options?.prompt?.label ?? options?.title ?? 'Value'}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') settle(value); }}
            />
          )}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(isPrompt ? null : CANCELLED)}>
              {options?.cancelLabel ?? 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(isPrompt ? value : true)}
              // --destructive/-foreground, not --status-danger/-foreground: the
              // latter pair is a tint token set, its foreground is meant for text
              // on --status-danger-bg. On the solid fill it reads 2.3:1 in light
              // and 1.8:1 in dark. This pair is what Button variant="destructive"
              // already uses everywhere else.
              style={options?.destructive
                ? { background: 'var(--destructive)', color: 'var(--destructive-foreground)' }
                : undefined}
            >
              {options?.confirmLabel ?? (options?.destructive ? 'Delete' : 'Confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

/** Async confirm. Resolves true when confirmed, false when dismissed. */
export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  return useCallback(
    async (options: ConfirmOptions) => {
      // No provider (a stray render tree, a test) must not silently confirm a
      // destructive action — refuse instead.
      if (!ctx) return false;
      return (await ctx.open({ ...options, prompt: undefined })) === true;
    },
    [ctx],
  );
}

/** Async confirm whose cancel button does something of its own, such as going
 *  to put right what the question is about first. Closing the dialog without
 *  either button resolves 'dismiss', so that path can simply stop. */
export function useConfirmChoice(): (options: ConfirmOptions) => Promise<ConfirmChoice> {
  const ctx = useContext(ConfirmContext);
  return useCallback(
    async (options: ConfirmOptions): Promise<ConfirmChoice> => {
      if (!ctx) return 'dismiss';
      const result = await ctx.open({ ...options, prompt: undefined });
      if (result === true) return 'confirm';
      return result === CANCELLED ? 'cancel' : 'dismiss';
    },
    [ctx],
  );
}

/** Async prompt. Resolves the entered string, or null when dismissed. */
export function usePrompt(): (options: ConfirmOptions & { prompt: NonNullable<ConfirmOptions['prompt']> }) => Promise<string | null> {
  const ctx = useContext(ConfirmContext);
  return useCallback(
    async (options: ConfirmOptions) => {
      if (!ctx) return null;
      const result = await ctx.open(options);
      return typeof result === 'string' ? result : null;
    },
    [ctx],
  );
}
